import React, { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { PRICING_TYPES } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { ActionButtons } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/ActionButtons"
import { DurationFormItems } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/DurationFormItems"
import { FlatRateFormItems } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/FlatRateFormItems"
import { PerPersonFormItems } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PerPersonFormItems"
import { ConfirmModal } from "ui/ConfirmModal"
import styles from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/EditPricesForm/EditPricesForm.module.css"

const PRICE_FIELDS = [
  "trip_duration",
  "trip_buffer_time",
  "price_per_person",
  "price_per_child",
  "price_per_infant",
  "price_per_passenger",
  "flat_rate",
  "included_guests",
  "children_allowed",
  "infants_allowed",
  "passengers_allowed",
  "extra_guests_allowed"
]

/**
 * Local-only price edit form — no API calls.
 * Save/delete commit to localPrices in SchedulingStep via callbacks.
 */
export const EditPricesForm = ({
  record,
  closeEditForm,
  isFlatRate,
  setDraftPrices,
  addNewPrice,
  form,
  expandedRowData,
  onPriceSave,
  onPriceDelete
}) => {
  const { t } = useTranslation()
  const [isConfirmOpened, setIsConfirmOpened] = useState(false)

  // Draft rows have a string UUID as id; server prices have a numeric id
  const isExistingPrice = !!(record?.id && typeof record.id === "number")

  const capturePrice = () => {
    const allFormValues = form.getFieldsValue(true)
    const priceData = {}
    PRICE_FIELDS.forEach((key) => {
      if (allFormValues[key] !== undefined) {
        priceData[key] = allFormValues[key]
      }
    })
    return {
      ...priceData,
      pricing_type: isFlatRate ? PRICING_TYPES.FLAT_RATE : PRICING_TYPES.PER_PERSON
    }
  }

  const savePrice = async (callback) => {
    try {
      const requiredFields = isFlatRate
        ? ["trip_duration", "flat_rate", "included_guests"]
        : ["trip_duration"]
      await form.validateFields(requiredFields)

      const priceData = capturePrice()

      if (isExistingPrice) {
        onPriceSave({ ...priceData, id: record.id })
      } else {
        // Draft: commit to localPrices and remove open row
        onPriceSave({ ...priceData, tempId: record.id })
        setDraftPrices((prev) => prev.filter((p) => p.id !== record.id))
      }

      callback?.()
    } catch (err) {
      console.error("[EditPricesForm] Save validation failed:", err)
    }
  }

  const onSave = () => savePrice(closeEditForm)

  const onSaveAndAddNew = () =>
    savePrice(() => {
      closeEditForm()
      addNewPrice()
    })

  const deletePrice = () => {
    if (isExistingPrice) {
      // Existing server price — mark for deletion via parent
      onPriceDelete({ id: record.id })
    } else if (record.tempId) {
      // Locally saved new price (has tempId, lives in localPrices) — remove via parent
      onPriceDelete({ tempId: record.tempId })
    } else {
      // Open draft row (string UUID id, not yet locally saved) — remove from draftPrices
      setDraftPrices((prev) => prev.filter((p) => p.id !== record.id))
    }
    setIsConfirmOpened(false)
    closeEditForm()
  }

  return (
    <div className={styles.wrapper}>
      <DurationFormItems form={form} expandedRowData={expandedRowData} />
      {isFlatRate ? (
        <FlatRateFormItems form={form} record={expandedRowData} />
      ) : (
        <PerPersonFormItems form={form} record={expandedRowData} />
      )}
      <ActionButtons
        onSave={onSave}
        onSaveAndAddNew={onSaveAndAddNew}
        onCancel={closeEditForm}
        onDelete={() => setIsConfirmOpened(true)}
      />
      <ConfirmModal
        open={isConfirmOpened}
        title={t("experiences.scheduling.sections.length_and_rates.delete_duration_title")}
        info={t("experiences.scheduling.sections.length_and_rates.delete_duration_info")}
        onCancel={() => setIsConfirmOpened(false)}
        onDelete={deletePrice}
      />
    </div>
  )
}

EditPricesForm.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  closeEditForm: PropTypes.func.isRequired,
  isFlatRate: PropTypes.bool,
  setDraftPrices: PropTypes.func.isRequired,
  addNewPrice: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  expandedRowData: PropTypes.object,
  onPriceSave: PropTypes.func.isRequired,
  onPriceDelete: PropTypes.func.isRequired
}
